import { handleServiceError } from '../utils/errorHandler';
import { requestEdgeFunction } from './edgeFunctionClient';

export interface FindAllOptions {
  filters?: { column: string; value: unknown }[];
  orderBy?: { column: string; ascending?: boolean };
  limit?: number;
}

const TABLE_FUNCTION_MAP: Record<string, string> = {
  students: 'students',
  leads: 'students',
  addresses: 'students',
  phone_numbers: 'students',
  anamnesis: 'students',
  parq: 'students',
  payment_accounts: 'payment-gateway',
  students_payment_accounts: 'payment-gateway',
  payment_history: 'payment-gateway',
  subscription_plans: 'list-paginated-subscription-plans',
};

interface DataActionResponse<T> {
  data: T;
}

export class BaseSupabaseService<T> {
  protected tableName: string;

  constructor(tableName: string) {
    this.tableName = tableName;
  }

  private getFunctionName(): string {
    const functionName = TABLE_FUNCTION_MAP[this.tableName];
    if (!functionName) {
      throw new Error(`Nenhuma Edge Function configurada para ${this.tableName}.`);
    }

    return functionName;
  }

  private async callDataAction<R>(
    action: 'data-find-all' | 'data-find-by-id' | 'data-create' | 'data-update' | 'data-delete',
    payload: Record<string, unknown>,
  ): Promise<R> {
    const functionName = this.getFunctionName();
    const response = await requestEdgeFunction<DataActionResponse<R>>(functionName, {
      method: 'POST',
      body: {
        action,
        payload: {
          resource: this.tableName,
          ...payload,
        },
      },
    });

    return response.data;
  }

  async findAll(options?: FindAllOptions): Promise<T[]> {
    try {
      const data = await this.callDataAction<T[]>('data-find-all', {
        filters: options?.filters ?? [],
        orderBy: options?.orderBy ?? null,
        limit: options?.limit ?? null,
      });
      return data ?? [];
    } catch (error) {
      handleServiceError(error, `buscar registros de ${this.tableName}`);
      return [];
    }
  }

  async findById(id: number | string): Promise<T | null> {
    try {
      return await this.callDataAction<T | null>('data-find-by-id', { id });
    } catch (error) {
      handleServiceError(error, `buscar registro de ${this.tableName}`);
      return null;
    }
  }

  async create(payload: Partial<T>): Promise<T> {
    try {
      return await this.callDataAction<T>('data-create', {
        record: payload as Record<string, unknown>,
      });
    } catch (error) {
      handleServiceError(error, `cadastrar em ${this.tableName}`);
      throw error;
    }
  }

  async update(id: number | string, payload: Partial<T>): Promise<T> {
    try {
      return await this.callDataAction<T>('data-update', {
        id,
        record: payload as Record<string, unknown>,
      });
    } catch (error) {
      handleServiceError(error, `atualizar registro de ${this.tableName}`);
      throw error;
    }
  }

  async delete(id: number | string): Promise<void> {
    try {
      await this.callDataAction<null>('data-delete', { id });
    } catch (error) {
      handleServiceError(error, `excluir registro de ${this.tableName}`);
      throw error;
    }
  }
}
